export const DEFAULT_MATRIX_SIZE = 5;
export const DEFAULT_SCORING_MODE = "multiplicative";
export const DEFAULT_BAND_THRESHOLDS = { low_max: 5, medium_max: 10, high_max: 15 };
export const DEFAULT_BAND_COLORS = {
  low: "#16a34a",
  medium: "#eab308",
  high: "#f97316",
  critical: "#dc2626",
};
export const DEFAULT_BANDS = [
  { name: "Low", max_score: 5, color: DEFAULT_BAND_COLORS.low },
  { name: "Medium", max_score: 10, color: DEFAULT_BAND_COLORS.medium },
  { name: "High", max_score: 15, color: DEFAULT_BAND_COLORS.high },
  { name: "Critical", max_score: null, color: DEFAULT_BAND_COLORS.critical },
];

const normalizeBandKey = (value) =>
  String(value || "")
    .trim()
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, "");

const toFiniteNumber = (value) => {
  const parsed = Number(value);
  return Number.isFinite(parsed) ? parsed : null;
};

const toNonEmptyText = (value, fallback = "") => {
  const text = String(value || "").trim();
  return text || fallback;
};

const normalizeBandThresholds = (raw) => {
  if (Array.isArray(raw)) {
    const bands = raw
      .map((entry) => {
        const name = toNonEmptyText(entry?.name);
        if (!name) return null;
        const max = entry?.max_score == null ? null : toFiniteNumber(entry?.max_score);
        return { name, max_score: max };
      })
      .filter(Boolean);
    if (bands.length >= 2) return bands;
  }

  const legacyLow = toFiniteNumber(raw?.low_max);
  const legacyMedium = toFiniteNumber(raw?.medium_max);
  const legacyHigh = toFiniteNumber(raw?.high_max);
  if (legacyLow != null && legacyMedium != null && legacyHigh != null) {
    return [
      { name: "Low", max_score: legacyLow },
      { name: "Medium", max_score: legacyMedium },
      { name: "High", max_score: legacyHigh },
      { name: "Critical", max_score: null },
    ];
  }

  return DEFAULT_BANDS.map((band) => ({ name: band.name, max_score: band.max_score }));
};

const normalizeBandColors = (raw, bands) => {
  const source = raw && typeof raw === "object" ? raw : {};
  const legacyLookup = {
    low: source.low || DEFAULT_BAND_COLORS.low,
    medium: source.medium || DEFAULT_BAND_COLORS.medium,
    high: source.high || DEFAULT_BAND_COLORS.high,
    critical: source.critical || DEFAULT_BAND_COLORS.critical,
  };
  const normalizedSource = Object.fromEntries(
    Object.entries(source).map(([key, value]) => [normalizeBandKey(key), String(value || "").trim()])
  );

  return bands.reduce((acc, band, index) => {
    const fallbackColor = DEFAULT_BANDS[Math.min(index, DEFAULT_BANDS.length - 1)]?.color || DEFAULT_BAND_COLORS.critical;
    const exact = String(source?.[band.name] || "").trim();
    const byKey = normalizedSource[normalizeBandKey(band.name)];
    const legacyFallback =
      index === 0
        ? legacyLookup.low
        : index === 1
          ? legacyLookup.medium
          : index === 2
            ? legacyLookup.high
            : legacyLookup.critical;
    acc[band.name] = exact || byKey || legacyFallback || fallbackColor;
    return acc;
  }, {});
};

export const buildDefaultCellScores = (matrixSize = DEFAULT_MATRIX_SIZE) => {
  const out = {};
  for (let likelihood = 1; likelihood <= matrixSize; likelihood += 1) {
    out[String(likelihood)] = {};
    for (let impact = 1; impact <= matrixSize; impact += 1) {
      out[String(likelihood)][String(impact)] = likelihood * impact;
    }
  }
  return out;
};

const normalizeCellScores = (cellScores, matrixSize) => {
  const normalized = buildDefaultCellScores(matrixSize);
  for (let likelihood = 1; likelihood <= matrixSize; likelihood += 1) {
    for (let impact = 1; impact <= matrixSize; impact += 1) {
      const score = Number(cellScores?.[String(likelihood)]?.[String(impact)]);
      if (Number.isFinite(score)) {
        normalized[String(likelihood)][String(impact)] = score;
      }
    }
  }
  return normalized;
};

export const normalizeMatrixConfig = (raw) => {
  const matrixSize = Number(raw?.matrix_size) || DEFAULT_MATRIX_SIZE;
  const scoringMode = raw?.scoring_mode === "cell_map" ? "cell_map" : DEFAULT_SCORING_MODE;
  const bands = normalizeBandThresholds(raw?.band_thresholds);
  const colors = normalizeBandColors(raw?.band_colors, bands);
  return {
    matrix_size: matrixSize,
    scoring_mode: scoringMode,
    cell_scores: normalizeCellScores(raw?.cell_scores, matrixSize),
    band_thresholds: bands,
    band_colors: colors,
    version: raw?.version ?? null,
  };
};

export const getCellScore = ({ scoringMode, cellScores, lik, imp }) => {
  const likelihood = Number(lik);
  const impact = Number(imp);
  if (!Number.isInteger(likelihood) || !Number.isInteger(impact)) return null;
  if (scoringMode === "cell_map") {
    const score = Number(cellScores?.[String(likelihood)]?.[String(impact)]);
    if (Number.isFinite(score)) return score;
  }
  return likelihood * impact;
};

export const scoreToBand = ({ score, bandThresholds }) => {
  const bands = normalizeBandThresholds(bandThresholds);
  if (!Number.isFinite(score)) return bands[0]?.name || "Low";
  for (const band of bands) {
    if (band?.max_score == null || Number(score) <= Number(band.max_score)) {
      return band?.name || "Unknown";
    }
  }
  return bands[bands.length - 1]?.name || "Critical";
};

export const bandToColor = ({ band, bandColors }) => {
  const palette = bandColors && typeof bandColors === "object" ? bandColors : {};
  const exact = String(palette?.[band] || "").trim();
  if (exact) return exact;
  const normalizedPalette = Object.fromEntries(
    Object.entries(palette).map(([key, value]) => [normalizeBandKey(key), String(value || "").trim()])
  );
  const normalized = normalizedPalette[normalizeBandKey(band)];
  if (normalized) return normalized;
  const legacy = {
    low: DEFAULT_BAND_COLORS.low,
    medium: DEFAULT_BAND_COLORS.medium,
    high: DEFAULT_BAND_COLORS.high,
    critical: DEFAULT_BAND_COLORS.critical,
  };
  return legacy[normalizeBandKey(band)] || DEFAULT_BAND_COLORS.critical;
};

export const getReadableTextColor = (backgroundHex) => {
  const hex = String(backgroundHex || "").replace("#", "");
  if (hex.length !== 6) return "#111827";
  const r = parseInt(hex.slice(0, 2), 16);
  const g = parseInt(hex.slice(2, 4), 16);
  const b = parseInt(hex.slice(4, 6), 16);
  if ([r, g, b].some((channel) => Number.isNaN(channel))) return "#111827";
  const luminance = (0.299 * r + 0.587 * g + 0.114 * b) / 255;
  return luminance > 0.55 ? "#111827" : "#f9fafb";
};
